// QL-217 — per-tab conditional display on a flow `tabs_block` layout.
//
// A tabs_block config carries the block's own `conditionalDisplay` plus an
// optional `conditionalDisplay` on each entry of `tabs`. Every place that
// reads or rewrites rules out of a layout config (write guards, deletion
// usage, flow copy, the public PII allow-list) walks `configConditionRules`
// so a tab rule is never missed, and every evaluator hides a tab's
// components through `hiddenTabIds`.

import type { ConditionalDisplayConfig } from "./conditional-display";

/** A `conditionalDisplay` object as stored: not validated, may be malformed. */
export type StoredConditionRule = Record<string, unknown>;

type RuleCarrier = {
  conditionalDisplay?: unknown;
  tabs?: unknown;
};

function asObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Parse a config given as a JSON string (stored) or an object (write input). */
export function parseRuleCarrier(config: unknown): RuleCarrier | null {
  if (typeof config === "string") {
    try {
      return asObject(JSON.parse(config));
    } catch {
      return null;
    }
  }
  return asObject(config);
}

/**
 * Every rule object in a parsed config: its own `conditionalDisplay`, then
 * each tab's, in tab order. Enabled or not. The objects are the config's
 * own, so a caller may rewrite them in place and re-serialize the carrier.
 */
export function carrierConditionRules(carrier: RuleCarrier | null): StoredConditionRule[] {
  if (!carrier) return [];
  const rules: StoredConditionRule[] = [];
  const own = asObject(carrier.conditionalDisplay);
  if (own) rules.push(own);
  if (Array.isArray(carrier.tabs)) {
    for (const tab of carrier.tabs) {
      const rule = asObject(asObject(tab)?.conditionalDisplay);
      if (rule) rules.push(rule);
    }
  }
  return rules;
}

/** `carrierConditionRules` for a config string or object. */
export function configConditionRules(config: unknown): StoredConditionRule[] {
  return carrierConditionRules(parseRuleCarrier(config));
}

/**
 * Ids of the tabs whose rule hides them. `isShown` is the caller's own
 * evaluator (each surface resolves field values its own way); a tab with no
 * rule, or a disabled one, is passed to it like any other and stays shown.
 */
export function hiddenTabIds(
  config: unknown,
  isShown: (rule: ConditionalDisplayConfig) => boolean,
): Set<string> {
  const hidden = new Set<string>();
  const carrier = parseRuleCarrier(config);
  if (!carrier || !Array.isArray(carrier.tabs)) return hidden;
  for (const tab of carrier.tabs) {
    const entry = asObject(tab);
    const rule = asObject(entry?.conditionalDisplay);
    if (!entry || typeof entry.id !== "string" || !rule) continue;
    if (!isShown(rule as ConditionalDisplayConfig)) hidden.add(entry.id);
  }
  return hidden;
}

/**
 * Ids of the layout components a page's block and tab rules hide. A hidden
 * block hides all of its components, a hidden tab only its own. Used by the
 * public flow, whose payload lists every layout component flat (each carries
 * `flowPageLayoutId` and `tabId`) with the layouts beside them. `isShown` is
 * the caller's evaluator, as for `hiddenTabIds`.
 */
export function hiddenLayoutComponentIds(
  layouts: ReadonlyArray<{ id: string; config?: string | null }>,
  components: ReadonlyArray<{
    id: string;
    flowPageLayoutId?: string | null;
    tabId?: string | null;
  }>,
  isShown: (rule: ConditionalDisplayConfig) => boolean,
): Set<string> {
  const hiddenBlocks = new Set<string>();
  const hiddenTabsByBlock = new Map<string, Set<string>>();
  for (const layout of layouts) {
    const own = asObject(parseRuleCarrier(layout.config)?.conditionalDisplay);
    if (own && !isShown(own as ConditionalDisplayConfig)) hiddenBlocks.add(layout.id);
    hiddenTabsByBlock.set(layout.id, hiddenTabIds(layout.config, isShown));
  }

  const hidden = new Set<string>();
  for (const component of components) {
    const blockId = component.flowPageLayoutId;
    if (!blockId) continue;
    const tabHidden = !!component.tabId && !!hiddenTabsByBlock.get(blockId)?.has(component.tabId);
    if (hiddenBlocks.has(blockId) || tabHidden) hidden.add(component.id);
  }
  return hidden;
}
